import { FolderContent } from '@/api/generatedApi';
import { BulkDeleteContentModal } from '@/components/modals/BulkDeleteContentModal';
import { BulkMoveContentsModal } from '@/components/modals/BulkMoveContentsModal/BulkMoveContentsModal';
import { BulkReprocessModal } from '@/components/modals/BulkReprocessModal';
import { TargetFolder } from '@/components/modals/BulkMoveContentsModal/targetFolderUtils';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import { isAsset } from '@/utils/assets';
import { pluralize } from '@/utils/format';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { Box, Collapse, Divider, IconButton, Tooltip } from '@mui/material';
import { ReactNode, useState } from 'react';
import { useBulkSelectionContext } from './BulkSelectionContext';

export const BulkActionsToolbar = <T extends FolderContent>({ children }: { children?: ReactNode }) => {
  const { permissions } = useGetCurrentUser();
  const bulkSelection = useBulkSelectionContext<T>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [reprocessModalOpen, setReprocessModalOpen] = useState(false);
  const [itemsToMove, setItemsToMove] = useState<T[]>([]);
  const [itemsToDelete, setItemsToDelete] = useState<T[]>([]);
  const [itemsToReprocess, setItemsToReprocess] = useState<T[]>([]);

  const selectedItems = bulkSelection?.selectedRows ?? [];
  const isLoadingAll = bulkSelection?.isLoadingAll ?? false;
  const clearSelection = bulkSelection?.clearSelection ?? (() => {});
  const selectedCount = selectedItems.length;
  const hasSelection = selectedCount > 0 || isLoadingAll;

  const getTooltipText = (readyText: string) => (isLoadingAll ? 'Data is loading' : readyText);

  const handleDeleteModalOpen = () => {
    setItemsToDelete(selectedItems);
    setDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setItemsToDelete([]);
  };

  const handleDeleteComplete = () => {
    clearSelection();
  };

  const handleMoveModalOpen = () => {
    setItemsToMove(selectedItems);
    setMoveModalOpen(true);
  };

  const handleMoveModalClose = () => {
    setMoveModalOpen(false);
    setItemsToMove([]);
    setTargetFolder(undefined);
  };

  const handleMoveComplete = () => {
    clearSelection();
  };

  const handleReprocessModalOpen = () => {
    setItemsToReprocess(selectedItems);
    setReprocessModalOpen(true);
  };

  const handleReprocessModalClose = () => {
    setReprocessModalOpen(false);
    setItemsToReprocess([]);
  };

  const handleReprocessComplete = () => {
    clearSelection();
  };

  const hasSelectedAssets = selectedItems.some((item: FolderContent) => isAsset(item));
  const showDeleteButton = !children && permissions.canDeleteAssets;
  const showMoveButton = !children && permissions.canCreateFolders;
  const showReprocessButton = !children && permissions.canReprocessAssets && (isLoadingAll || hasSelectedAssets);

  const [targetFolder, setTargetFolder] = useState<TargetFolder>();

  return (
    <>
      <Collapse in={hasSelection} orientation='horizontal' timeout={300} unmountOnExit>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Divider orientation='vertical' flexItem sx={{ mx: 0.5, height: 16, alignSelf: 'center' }} />
          {children ?? (
            <>
              {showMoveButton && (
                <Tooltip title={getTooltipText(`Move ${pluralize(selectedCount, 'selected item', 'selected items')}`)}>
                  <span>
                    <IconButton
                      size='small'
                      onClick={handleMoveModalOpen}
                      disabled={isLoadingAll}
                      sx={{ color: 'success.main' }}
                    >
                      <DriveFileMoveIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {showDeleteButton && (
                <Tooltip
                  title={getTooltipText(`Delete ${pluralize(selectedCount, 'selected item', 'selected items')}`)}
                >
                  <span>
                    <IconButton
                      size='small'
                      color='error'
                      onClick={handleDeleteModalOpen}
                      disabled={isLoadingAll}
                      data-testid={AssetsPageObjectModel.BulkDeleteButton}
                    >
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {showReprocessButton && (
                <Tooltip title={getTooltipText('Reprocess assets')}>
                  <span>
                    <IconButton
                      size='small'
                      onClick={handleReprocessModalOpen}
                      disabled={isLoadingAll}
                      data-testid={AssetsPageObjectModel.BulkReprocessButton}
                    >
                      <AutorenewIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </Collapse>
      <BulkMoveContentsModal
        isOpen={moveModalOpen}
        onClose={handleMoveModalClose}
        contents={itemsToMove as FolderContent[]}
        targetFolder={targetFolder}
        selectionMode
        onTargetSelected={setTargetFolder}
        onMoveComplete={handleMoveComplete}
      />
      <BulkDeleteContentModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        selectedContent={itemsToDelete as FolderContent[]}
        onDeleteComplete={handleDeleteComplete}
      />
      <BulkReprocessModal
        open={reprocessModalOpen}
        onClose={handleReprocessModalClose}
        selectedContent={itemsToReprocess as FolderContent[]}
        onReprocessComplete={handleReprocessComplete}
      />
    </>
  );
};
